<script setup lang="ts">
import { useForm } from '@formwerk/core'

// One logo GIF per colour: public/signature/logo-<color>.gif.
const availableColors = [
  { name: 'Wine', value: 'wine' },
  { name: 'Sage', value: 'sage' },
  { name: 'Lilac', value: 'lilac' },
  { name: 'Black', value: 'black' },
] as const satisfies { name: string, value: SignatureColor }[]

const { values, formProps, reset } = useForm({
  initialValues: {
    fullname: '',
    role: '',
    email: '',
    phone: '',
    color: 'wine' as SignatureColor,
  },
})

const signatureValues = computed<SignatureValues>(() => ({
  fullname: values.fullname ?? '',
  role: values.role ?? '',
  email: values.email ?? '',
  phone: values.phone ?? '',
  color: values.color ?? 'wine',
}))

const copyAutoReset = refAutoReset(false, 2000)
const copyAutoResetHtml = refAutoReset(false, 2000)

// One Outlook-safe HTML for every client, with the zero-width character iOS
// Mail needs to keep an image-only signature on paste.
const signatureHtml = useOutlookSignatureHtml(signatureValues)
const signatureImageHtml = useOutlookImageSignatureHtml(signatureValues)

const tabs = [
  { id: 'custom', label: 'Custom Signature', shortLabel: 'Custom' },
  { id: 'image', label: 'Single Image Signature (legacy)', shortLabel: 'Image (legacy)' },
] as const

type TabId = typeof tabs[number]['id']

// `activeTab` drives the tab buttons right away; `displayedTab` drives the panel
// and only flips once the panel has faded out, so preview and actions swap together.
const TAB_FADE_MS = 200
const activeTab = ref<TabId>('custom')
const displayedTab = ref<TabId>('custom')
const panelHidden = ref(false)

// Every action (and the preview) uses the same Outlook-safe markup. See
// docs/outlook-signature.md.
const activeSignatureHtml = computed(() => ({
  custom: signatureHtml.value,
  image: signatureImageHtml.value,
})[displayedTab.value])

let tabTimer: ReturnType<typeof setTimeout> | undefined

function selectTab(id: TabId) {
  if (id === activeTab.value)
    return
  activeTab.value = id
  panelHidden.value = true
  clearTimeout(tabTimer)
  tabTimer = setTimeout(() => {
    displayedTab.value = activeTab.value
    panelHidden.value = false
  }, TAB_FADE_MS)
}

onBeforeUnmount(() => clearTimeout(tabTimer))

function copySignatureHtml(html: string, onCopied: () => void) {
  const { fullname, role } = signatureValues.value
  const plainText = ['The No. J', [fullname, role].filter(Boolean).join(' – ')]
    .filter(Boolean)
    .join('\n')
  if (copyRawHtml(html, plainText))
    onCopied()
}

function copySignature() {
  copySignatureHtml(activeSignatureHtml.value, () => copyAutoReset.value = true)
}

// Mobile and tablet (below lg): secondary actions live in a bottom sheet
const moreOpen = ref(false)
const sheetActionClass = 'typo-sans-2 c-black px-3 py-3.5 rounded-2 text-left flex gap-3 w-full cursor-pointer items-center hover:bg-linen'

onKeyStroke('Escape', () => {
  moreOpen.value = false
})

function closeMoreAfter(action: () => unknown) {
  action()
  moreOpen.value = false
}

const head = `<!doctype html>
  <html xmlns="http://www.w3.org/1999/xhtml">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <style>
        img { border: 0; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
        a { text-decoration: none; }
        a img { border: 0; outline: none; }
      </style>
      <!--[if mso]>
      <style>
        img { border: 0 !important; }
        a img { border: 0 !important; }
      </style>
      <![endif]-->
    </head>
    <body>
`

const tail = `
    </body>
  </html>
`

function copyHtmlSignature(onCopied: () => void) {
  const wrappedHtml = head + activeSignatureHtml.value + tail
  navigator.clipboard.writeText(wrappedHtml).then(() => {
    onCopied()
  }).catch((err) => {
    console.error('Failed to copy text: ', err)
  })
}

function download(href: string, filename: string) {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
}

function downloadSignature() {
  const blob = new Blob([head + activeSignatureHtml.value + tail], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  download(url, 'signature.html')
  URL.revokeObjectURL(url)
}

async function downloadSignatureGif() {
  const img = document.querySelector('.sign-image img') as HTMLImageElement
  if (!img)
    return
  const blob = await $fetch<Blob>(img.src, { responseType: 'blob' })
  const url = URL.createObjectURL(blob)
  download(url, 'signature.gif')
  URL.revokeObjectURL(url)
}
</script>

<template>
  <div class="mx-auto my-8 px-4 pb-12 max-w-240 w-full sm:px-6">
    <form v-bind="formProps" novalidate @submit.prevent>
      <div class="gap-x-3 gap-y-4 grid grid-cols-1 md:grid-cols-2">
        <TextField label="Full Name" name="fullname" />
        <TextField label="Role" name="role" />
        <TextField label="Email" name="email" type="email" />
        <TextField label="Phone" name="phone" type="tel" />

        <RadioGroupField label="Colour" name="color" class="col-span-full">
          <RadioButton v-for="option in availableColors" :key="option.value" :label="option.name" :value="option.value" />
        </RadioGroupField>
      </div>
    </form>

    <!-- Mobile: full-width segmented control with short labels. sm+: pills. -->
    <div class="mt-8 p-1 rounded-2.5 bg-linen grid grid-cols-2 sm:p-0 sm:bg-transparent sm:flex sm:flex-wrap sm:gap-2" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="typo-sans-1 px-4 motion-snug rounded-2.5 h-7.5 cursor-pointer uppercase"
        :class="activeTab === tab.id
          ? 'bg-[var(--primary-color)] c-white'
          : 'c-black/60 hover:c-black sm:c-[var(--primary-color)] sm:bg-[var(--negative-color)] sm:hover:bg-[color-mix(in_srgb,var(--negative-color)_80%,transparent)]'"
        :aria-selected="activeTab === tab.id"
        @click="selectTab(tab.id)"
      >
        <span class="sm:hidden">{{ tab.shortLabel }}</span>
        <span class="hidden sm:inline">{{ tab.label }}</span>
      </button>
    </div>

    <!-- duration-200 must match TAB_FADE_MS -->
    <div
      class="mt-4 transition-opacity duration-200 ease"
      :class="{ 'opacity-0 pointer-events-none': panelHidden }"
    >
      <div class="p-3 rounded-2.5 bg-linen sm:p-4">
        <div class="mb-2 flex items-center justify-between">
          <p class="typo-sans-1 c-black/50 uppercase">
            Signature Preview:
          </p>
          <button
            type="button"
            class="typo-sans-1 c-black/50 flex gap-1 cursor-pointer uppercase items-center hover:c-black lg:hidden"
            @click="reset()"
          >
            <span class="i-icon-arrow-counter-clockwise size-3.5 icon" aria-hidden="true" />
            Reset
          </button>
        </div>
        <!--
          v-show, not v-if: the legacy image gets its size during SSR, so
          keeping it mounted avoids a resize when switching tabs.
          overflow-x-auto: the email table has a fixed width that can exceed narrow phones.
        -->
        <div class="p-3 rounded-1.5 bg-white overflow-x-auto sm:p-4">
          <!-- The exact HTML Copy Signature puts on the clipboard -->
          <div
            v-show="displayedTab === 'custom'"
            class="sign"
            v-html="signatureHtml"
          />
          <TheSignatureImage
            v-show="displayedTab === 'image'"
            class="sign-image"
            v-bind="signatureValues"
          />
        </div>
      </div>

      <!-- Keyed so the buttons are recreated with the right theme instead of animating between themes -->
      <div :key="displayedTab" class="mt-6">
        <!-- Mobile and tablet (below lg): one primary action, the rest in the "More" sheet -->
        <div class="flex gap-2 lg:hidden">
          <Button title="Copy Signature" theme="primary" class="flex-1 [&>span]:h-12 [&>span]:w-full" @click="copySignature">
            <SwapLabel :active="copyAutoReset" label="Copy Signature" active-label="Copied!" class="typo-sans-1 uppercase" />
          </Button>
          <Button title="More actions" theme="negative" class="[&>span]:px-4.5 [&>span]:h-12" :aria-expanded="moreOpen" @click="moreOpen = true">
            <span class="i-icon-dots-three-bold size-4 icon" aria-hidden="true" />
          </Button>
        </div>

        <div class="flex-wrap gap-3 hidden items-center justify-between lg:flex">
          <ButtonLink title="Reset" @click="reset()" />

          <div class="flex flex-wrap gap-2">
            <Button title="Copy Signature" theme="negative" @click="copySignature">
              <SwapLabel :active="copyAutoReset" label="Copy Signature" active-label="Copied!" class="typo-sans-1 uppercase" />
            </Button>
            <Button title="Copy HTML" theme="negative" @click="copyHtmlSignature(() => copyAutoResetHtml = true)">
              <SwapLabel :active="copyAutoResetHtml" label="Copy HTML" active-label="Copied!" class="typo-sans-1 uppercase" />
            </Button>
            <Button title="Download HTML" :theme="displayedTab === 'custom' ? 'primary' : 'negative'" @click="downloadSignature()">
              <span class="typo-sans-1 uppercase">Download HTML</span>
            </Button>
            <Button v-if="displayedTab === 'image'" title="Download GIF" theme="primary" @click="downloadSignatureGif">
              <span class="typo-sans-1 uppercase">Download GIF</span>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-250"
        leave-active-class="transition-opacity duration-250"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div v-if="moreOpen" class="bg-black/40 inset-0 fixed z-50 lg:hidden" @click="moreOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-custom-circ"
        leave-active-class="transition-transform duration-250 ease-custom-circ"
        enter-from-class="translate-y-full"
        leave-to-class="translate-y-full"
      >
        <div
          v-if="moreOpen"
          class="mx-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 rounded-t-4 bg-ivory inset-x-0 bottom-0 fixed z-50 sm:max-w-120 lg:hidden"
          role="dialog"
          aria-label="More actions"
        >
          <div class="mx-auto mb-3 rounded-full bg-black/15 h-1 w-10" aria-hidden="true" />
          <button
            type="button"
            :class="sheetActionClass"
            @click="copyHtmlSignature(() => copyAutoResetHtml = true)"
          >
            <span class="i-icon-code size-5 icon" aria-hidden="true" />
            <SwapLabel :active="copyAutoResetHtml" label="Copy HTML" active-label="Copied!" />
          </button>
          <button
            type="button"
            :class="sheetActionClass"
            @click="closeMoreAfter(() => downloadSignature())"
          >
            <span class="i-icon-file-html size-5 icon" aria-hidden="true" />
            Download HTML
          </button>
          <button
            v-if="displayedTab === 'image'"
            type="button"
            :class="sheetActionClass"
            @click="closeMoreAfter(downloadSignatureGif)"
          >
            <span class="i-icon-file-png size-5 icon" aria-hidden="true" />
            Download GIF
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
